import type { Signals, VisitorIdentity } from '@fraudeai/schema';
import { hash, hashValue } from './util/hash.js';

/**
 * Стабильный идентификатор посетителя.
 *
 * Собирается только из компонентов, устойчивых между перезагрузками. Ключевое
 * решение: компонент, который сам себе противоречит внутри одной сессии
 * (canvas с внесённым шумом, «плавающий» аудио-хеш), в идентификатор не
 * попадает вовсе. Иначе рандомизатор давал бы новый ID при каждом заходе, и
 * получалось бы, что подмена улучшает приватность именно от нашего чекера.
 *
 * `confidence` показывает, сколько компонентов удалось использовать. Низкая
 * уверенность — это не «чисто», а «нечего измерять».
 */

interface Component {
  id: string;
  hash: string | null;
  /** Компонент не менялся между повторными измерениями. */
  stable: boolean;
  /** Вес в итоговой уверенности. */
  weight: number;
}

export function computeIdentity(signals: Partial<Signals>): VisitorIdentity {
  const {
    canvas,
    webgl,
    webgpu,
    audio,
    fonts,
    os,
    engine,
    locale,
    screen,
    navigator: nav,
    rects,
  } = signals;

  const components: Component[] = [
    {
      id: 'canvas',
      hash: canvas?.combinedHash ?? null,
      stable: canvas?.stable !== false,
      weight: 3,
    },
    {
      id: 'webgl',
      hash: webgl
        ? hashValue({
            renderer: webgl.v2?.unmaskedRenderer ?? webgl.v1?.unmaskedRenderer ?? null,
            vendor: webgl.v2?.unmaskedVendor ?? webgl.v1?.unmaskedVendor ?? null,
            params: webgl.v2?.parameters ?? webgl.v1?.parameters ?? null,
            precision: webgl.v2?.shaderPrecision ?? webgl.v1?.shaderPrecision ?? null,
            extensions: webgl.v2?.extensions ?? webgl.v1?.extensions ?? null,
          })
        : null,
      stable: (webgl?.v1?.stable ?? true) !== false && (webgl?.v2?.stable ?? true) !== false,
      weight: 4,
    },
    { id: 'webgpu', hash: webgpu?.profileHash ?? null, stable: true, weight: 2 },
    { id: 'audio', hash: audio?.hash ?? null, stable: audio?.stable !== false, weight: 2 },
    { id: 'fonts', hash: fonts?.hash ?? null, stable: true, weight: 3 },
    { id: 'voices', hash: os?.voicesHash ?? null, stable: true, weight: 2 },
    { id: 'codecs', hash: os?.codecsHash ?? null, stable: true, weight: 1 },
    { id: 'math', hash: engine?.mathHash ?? null, stable: true, weight: 1 },
    { id: 'rects', hash: rects?.hash ?? null, stable: rects?.stable !== false, weight: 1 },
    {
      id: 'screen',
      hash: screen
        ? hashValue([screen.width, screen.height, screen.colorDepth, screen.devicePixelRatio])
        : null,
      stable: true,
      weight: 1,
    },
    {
      id: 'platform',
      hash: nav
        ? hashValue({
            platform: nav.platform,
            hardwareConcurrency: nav.hardwareConcurrency,
            deviceMemory: nav.deviceMemory,
            languages: nav.languages,
            timezone: locale?.timezone ?? null,
          })
        : null,
      weight: 2,
      stable: true,
    },
  ];

  const usable = components.filter((c) => c.hash !== null && c.stable);
  const totalWeight = components.reduce((sum, c) => sum + c.weight, 0);
  const usableWeight = usable.reduce((sum, c) => sum + c.weight, 0);

  const visitorId = usable.length
    ? hash(usable.map((c) => `${c.id}:${c.hash}`).join('|')).slice(0, 24)
    : 'unstable';

  return {
    visitorId,
    confidence: totalWeight === 0 ? 0 : Math.round((usableWeight / totalWeight) * 100) / 100,
    components: components
      .filter((c) => c.hash !== null)
      .map((c) => ({ id: c.id, hash: c.hash as string, stable: c.stable })),
  };
}
